import type {
  QuotesSummaryResponse,
  QuotesSummaryPosition,
} from '../../../../shared/api/quotesSelection.api'
import type { QuoteFilter } from '../hooks/useQuotesSelection'

interface Props {
  summary: QuotesSummaryResponse['summary']
  positions: QuotesSummaryPosition[]
  localSelection: Record<string, string | null>
  filter: QuoteFilter
  onFilterChange: (f: QuoteFilter) => void
}

/**
 * 4 метрики сверху страницы. Показывают локальный подсчёт с учётом
 * несохранённых выборов менеджера — чтобы счётчик «Квота выбрана» реагировал
 * мгновенно, а не только после POST.
 */
export function QuotesSummaryCards({
  summary,
  positions,
  localSelection,
  filter,
  onFilterChange,
}: Props) {
  const local = {
    total: summary.total,
    selected: positions.filter((p) => (localSelection[p.id] ?? null) !== null).length,
    waiting: positions.filter((p) => (localSelection[p.id] ?? null) === null && p.quotes.length > 0)
      .length,
    noQuotes: summary.noQuotes,
  }

  const cards: Array<{
    key: QuoteFilter
    label: string
    value: string
    bg: string
    ring: string
  }> = [
    {
      key: 'all',
      label: 'Позиций',
      value: String(local.total),
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      ring: 'ring-gray-300 dark:ring-gray-600',
    },
    {
      key: 'ready',
      label: 'Квота выбрана',
      value: `${local.selected} / ${local.total}`,
      bg: 'bg-green-50 dark:bg-green-900/20',
      ring: 'ring-green-400 dark:ring-green-600',
    },
    {
      key: 'waiting',
      label: 'Ждут выбора',
      value: String(local.waiting),
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      ring: 'ring-amber-400 dark:ring-amber-600',
    },
    {
      key: 'no-quotes',
      label: 'Без квот',
      value: String(local.noQuotes),
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      ring: 'ring-gray-300 dark:ring-gray-600',
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {cards.map((c) => {
        const active = filter === c.key
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onFilterChange(c.key)}
            className={`text-left rounded-lg px-4 py-3 transition-colors ${c.bg} ${
              active ? `ring-2 ${c.ring}` : 'ring-1 ring-transparent hover:ring-1 hover:' + c.ring
            }`}
          >
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {c.label}
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {c.value}
            </div>
          </button>
        )
      })}
    </div>
  )
}
